---
demo:
  concept:
    title: |-
      LOGBOOK Concept
    start1: |-
      LOGBOOK Concept
    start2: |-
      A logbook is a chronological report about the **positions of a ship during a voyage** – the ship’s movement and the events on board in space and time.
    timelineOverview: |-
      In LOGBOOK, the ship time is displayed in the form of a **Timeline**. 
      The timeline is a continuous **vertical timeline** that runs **from top to bottom**.

    timelineYears_title: |-
      LOGBOOK Concept: Overview Timeline
    timelineYears: |-
      The timeline begins in the **first year** for which there is an entry and extends to the end of the **current year**. All of your **voyages** are arranged along this timeline.

    timelineYears2_title: |-
      LOGBOOK Concept: Overview Timeline   
    timelineYears2: |-
      Instead of the confusing page-turning between times of day in analog logbooks, there is intuitive **scrolling** and **zooming** in the digital timeline.

    timelineYears3_title: |-
      LOGBOOK Concept: Overview Timeline
    timelineYears3: |-
      In **Overview mode**, the timeline displays **years** and **months**. Each month is indicated by a small line.
      As soon as there are log entries for a year, the timeline is stretched for that year.  

    timelineYears4_title: |-
      LOGBOOK Concept: Overview Timeline    
    timelineYears4: |-
      To the right of the timeline, **voyages** appear with name and time period, and the relevant **time period** is marked in color on the timeline.

    timelineVoyageClick_title: |-
      LOGBOOK Concept: Overview Timeline  
    timelineVoyageClick: |-
      By **clicking on a voyage**, LOGBOOK switches from overview mode to displaying the selected voyage. 
      
    timelineVoyage1_title: |-
      LOGBOOK Concept: Voyage View   
    timelineVoyage1: |-
      LOGBOOK zooms into the timeline and shows only a section of the entire timeline, ranging from the **first to the last day** of the selected **voyage**. 

    timelineVoyage2_title: |-
      LOGBOOK Concept: Voyage View   
    timelineVoyage2: |-
      Each **voyage** can contain a number of **passages** where each passage can contain a number of **days**. All days are listed along the timeline in chronological order.

    timelineVoyage3_title: |-
      LOGBOOK Concept: Voyage View  
    timelineVoyage3: |-
      The button to the left of the voyage name shows and hides the **voyage's data sheet**. 
      
    timelineVoyage4_title: |-
      LOGBOOK Concept: Voyage View   
    timelineVoyage4: |-
      The data sheet contains multiple cards. On the **Voyage** card, the voyage can be given a name. 

    timelineVoyage5_title: |-
      LOGBOOK Concept: Voyage View   
    timelineVoyage5: |-
      The **Statistics** card provides a summary and analysis of the voyage.

    timelineVoyage6_title: |-
      LOGBOOK Concept: Voyage View   
    timelineVoyage6: |-
      The **Track** card shows the track of the entire voyage on a nautical chart. On the **Voyage plan** card, there is the opportunity to plan the course of the voyage in advance.

    timelineVoyage7_title: |-
      LOGBOOK Concept: Voyage View   
    timelineVoyage7: |-
      The **button on the top right** of the voyage area brings you back to the overview timeline. 

    timelineVoyage8_title: |-
      LOGBOOK Concept: Voyage View   
    timelineVoyage8: |-
      And the button on the left closes the dataview of the voyage.

    timelineVoyage9_title: |-
      LOGBOOK Concept: Voyage View   
    timelineVoyage9: |-
      Directly below the voyage area begins the first **passage** of the voyage. 

    timelinePassage1_title: |-
      LOGBOOK Concept: The Passage 
    timelinePassage1: |-
      All **days of the passage** are displayed along the timeline. The period during which the ship was underway on the respective day is highlighted in color on the timeline. 

      The lines divide the day into 4-hour sections.

    timelinePassage2_title: |-
      LOGBOOK Concept: The Passage 
    timelinePassage2: |-
      Clicking the arrow to the left of the name displays the **passage's data sheet** with multiple cards with data, settings, and displays. 
      Here you can see the **Statistics** card with a summary and analysis of the passage.

    timelinePassage3_title: |-
      LOGBOOK Concept: The Passage 
    timelinePassage3: |-
      Clicking on the **date of a day** opens the day.


    timelineDay1_title: |-
      LOGBOOK Concept: The Day
    timelineDay1: |-
      It receives a **color-highlighted header** and all **log entries** recorded during the day are listed along the timeline, which is further stretched in this area.

    timelineDay2_title: |-
      LOGBOOK Concept: The Day
    timelineDay2: |-
      **Each hour of the day** is marked by a **line**, each 4 hours are somewhat longer, and for 0:00 h and 12:00 h even longer. This makes it easy to see at a glance how the **log entries** are distributed throughout the day.

    timelineDay3_title: |-
      LOGBOOK Concept: The Day
    timelineDay3: |-
      The  arrow in the colored header displays the **day's data sheet** with various cards instead of the list of **log entries**.

    timelineDay4_title: |-
      LOGBOOK Concept: The Day
    timelineDay4: |-
      The card **Day** allows to enter information on the day, like persons in the crew, when starting and arriving and more.

    timelineDay5_title: |-
      LOGBOOK Concept: The Day
    timelineDay5: |-
      Some of the cards like **Statistics**, **Track** and **Weather** show evaluations …

    timelineDay6_title: |-
      LOGBOOK Concept: The Day
    timelineDay6: |-
      … others like **Journal** and **Technic** allow to enter individual text and photos.

    timelineDay7_title: |-
      LOGBOOK Concept: The Day
    timelineDay7: |-
      By closing the day's data sheet with the toogle button on the top right of the data sheet ..., 

    timelineLog1_title: |-
      LOGBOOK Concept: The Log Entry
    timelineLog1: |-
      … you return to the list with all **log entries** of the day, the smallest unit in LOGBOOK. 
      
    timelineLog2_title: |-
      LOGBOOK Concept: The Log Entry
    timelineLog2: |-  
      The **log entry** contains NMEA data and additional information relating to a specific point in time.

    timelineLog3_title: |-
      LOGBOOK Concept: The Log Entry
    timelineLog3: |-
      Clicking on a **log entry** displays the **log entry's data sheet**.

    timelineLog4_title: |-
      LOGBOOK Concept: The Log Entry
    timelineLog4: |-
      The selected **log entry** shows a **color-highlighted header**. On the **log entry's data sheet**, the **automatically captured data** is displayed and for the current log entry additional data can be entered.

    timelineLog5_title: |-
      LOGBOOK Concept: The Log Entry
    timelineLog5: |-
      Which fields contain not editable, automatically entered data and which fields allow **manual data entry** depends on the **data present in the NMEA**.   

    final1_title: |-
      LOGBOOK – Your companion at sea
    final1: |-
      **LOGBOOK** is your faithful companion on every voyage – always by your side, from the first log entry to the last.

      **LOGBOOK** accompanies you through every passage, every day at sea, and keeps your ship's story alive.

    final2_title: |-
      Learn more about LOGBOOK
    final2: |-
      Check out our other screencasts and learn more about the use of **LOGBOOK** in daily practice. (More screencasts coming soon.)


  quickstart:
    title: |-
      LOGBOOK QuickStart – One time Preparations 
    start: |-
      This quickstart will help you to **get started with LOGBOOK** in just a few steps. It is a shortend version of the **QuickStart Guide** in our documentation. For more detailed instructions and tips, please check out this.

    start1_title: |-  
      Login  
    start1: |-
      **Login** to your account in the **LOGBOOK cloud** at 
      
      **https://logbook.yachts/app**.

    start2_title: |-  
      Login
    start2: |-
      You are now logged in your personal **Account settings**. 
      
    start3_title: |-  
      Initial Setup (one-time only)
    start3: |-
      Before you can keep your first logbook with LOGBOOK, two steps are required (one-time only): 
      
      1 -  **Create a ship**.

      2 -  **Establish and analyze the connection to the ship's NMEA** or to the device's integrated GPS.

    createShip_title: |-
      Create a ship
    createShip: |-
      First, LOGBOOK needs some information about your **ship** that affects the basic configuration of the logbook. Therefore, when starting LOGBOOK for the first time, the **Create new Ship** view is displayed.
      
      Enter the **ship's name** here, select the **ship type** (sailboat/motorboat), and set the **number of engines**.

    createShip2_title: |-
      Create a ship
    createShip2: |-
      Then close the view with the **Create Ship** button. Closing is only possible when all three fields are filled in.
      
    createShip3_title: |-
      Create a ship
    createShip3: |-
      Use the link in the bottom of the text to switch to the **LOGBOOK app**.
      
    createShip4_title: |-
      Create a ship
    createShip4: |-
      In the LOGBOOK app, you now see the **Overview timeline** for your ship. 
      
      In the **Ship section** the ship type and ship name as well as some buttons are displayed. All additional information about the ship can be entered at any time later in the **Ship data** view.

      In this demo the MMSI and call sign had been already entered.
      
    connectNMEA_title: |-
      Set Up NMEA Connection
    connectNMEA: |-
      First, **turn on** your NMEA network or GPS and **connect your device** to the NMEA network/GPS. 

    connectNMEA2_title: |-
      Set Up NMEA Connection
    connectNMEA2: |-
      The device, which is connected to the NMEA, is called the **Primary Device** with the **Primary LOGBOOK**. The Primary LOGBOOK is the main logbook that receives the NMEA data and creates log entries. 

    connectNMEA3_title: |-
      Set Up NMEA Connection
    connectNMEA3: |-
      Now choose **Settings** from the menu of the **NMEA** button (on the right in the Ship section). If no connection has been set up yet, the button shows a red marker <span style="color: var(--red); font-size: 1.5em;">□</span>.   

    connectNMEA4_title: |-
      Set Up NMEA Connection
    connectNMEA4: |-
      On the first card of the dialog go to **NMEA connection type** and select which protocol or connection type connects the NMEA network to your device (**USB** or WiFi/Ethernet with **TCP**). 
      
      If you can choose how to connect your NMEA to your device: TCP is better than USB.  

    connectNMEA5_title: |-
      Set Up NMEA Connection
    connectNMEA5: |-
      Then make the settings in the additional fields displayed for the connection type (Address, Port, etc.). You can find the required data in the documentation of the gateway, multiplexer, or plotter. For **USB** the available devices are listed under **Device**.

      If your device has an **internal GPS**, there will be an extra option.

    connectNMEA6_title: |-
      Test Connection
    connectNMEA6: |-
      Use the button **Test Connection** to switch to the next card of the dialog and start testing the connection.
      
    connectNMEA7_title: |-
      Test Connection
    connectNMEA7: |-
     LOGBOOK starts **data recording** via the connection set on the first card of the dialog. It may take a moment until the **first data is received**. 

    connectNMEA8_title: |-
      Test Connection
    connectNMEA8: |-
      If at least **UTC** and **GPS position** show a green dot on the **Test and Data** card, you can close the dialog with **Complete**.  

    connectNMEA9_title: |-
      Test Connection
    connectNMEA9: |-
      The green, unfilled marker <span style="color: var(--green); font-size: 1.5em;">□</span> in the **NMEA button** as well as the orange dot  <span style="color: var(--orange); font-size: 1.5em; vertical-align: bottom;">●</span> for **Automatic logging** menu entry show that the connection to the NMEA network is established and **data is being received**, but no regular automatic log entries are being created yet.  

      Now the one-time preparations are completed and you are ready to start your **first passage** and to keep your first logbook with LOGBOOK.  

    final2_title: |-
      Learn more about LOGBOOK
    final2: |-
      Check out our other screencasts and learn more about the use of **LOGBOOK** in daily practice. (More screencasts coming soon.) 


  quickstartVoyage:
    startVoyage_title: |-
      LOGBOOK QuickStart – Start Your First Passage
    startVoyage: |-
      This quickstart will show you how to **record your first passage** with LOGBOOK. It is a shortend version of the **QuickStart Guide** in our documentation. For more detailed instructions and tips, please check out this.

    startPassage_title: |-
      Start Passage
    startPassage: |-
      Switch to the **focus view** with the **Focus button** at the top in the **Ship section** or with the **Focus button** in the timeline. The focus view is the view for editing during sailing. Entries in the day's **data sheet Day** and in **log entries** can only be made while in focus view.        

    startPassage2_title: |-
      Start Passage
    startPassage2: |-
      When starting LOGBOOK for the first time, a **first voyage** and a **first passage** are already prepared so that you can start data recording as quickly as possible.   

    startPassage3_title: |-
      Start Passage
    startPassage3: |-
      LOGBOOK automatically uses the system timezone of the **Primary Device** as the **ship timezone**. If you want to use a different timezone for the passage, you can set it in the **Ship settings**. For guidance, the associated **current ship time** is also displayed here in addition to the timezone. 

    startPassage4_title: |-
      Start Passage
    startPassage4: |-
      Enter a name for the passage and start the passage with the **Start Passage** button. 

    startPassage5_title: |-
      Start Passage
    startPassage5: |-
      In the **focus view**, the screen display focuses on the **current day** and the **log row**, the colored highlighted row directly below the last log entry. The log row displays the **values currently read from the NMEA**. 

    dayData_title: |-
      Enter Data for the Day
    dayData: |-
      Before setting off, you should enter some important data on the **day data sheet** on the **Day** card. These are the **start location**, the **skipper**, and the persons in the **crew** and **passengers**, if applicable. The persons must be entered on the current day because they are relevant to the evidence.  

    dayData2_title: |-
      Enter Data for the Day
    dayData2: |-      
      For **Skipper**, **Crew** and **Passengers** you can select persons from your list of all persons. With a new, empty logbook, the person list is still empty. However, persons can be easily **created during input** using the **Add Person** entry at the top of the selection list. 

    engine1_title: |-
      Departing
    engine1: |-      
      Start your engines. If your NMEA contains **engine data**, LOGBOOK automatically detects when an engine is turned on or off and creates a log entry for this event. The status of the engine is displayed in the log entry with the engine buttons.  

    engine2_title: |-
      Departing
    engine2: |-      
      The second engine is now running.    

    departing_title: |-
      Departing
    departing: |-      
      LOGBOOK now **captures and analyzes NMEA data** every 30 seconds. As soon as the **ship starts moving**, LOGBOOK automatically creates the **first log entry** and the ship status displayed to the left of the timeline changes to **<span class="LogbookIcon-status-inmotion"></span> In motion**. 

    departing2_title: |-
      Departing
    departing2: |-      
      After departure, the **NMEA menu button** is filled and green and the **Automatic logging** menu entry shows a green dot <span style="color: var(--green); font-size: 1.5em;">●</span>. This means that automatic log entries are now active. 
      
    logEntries2_title: |-
      Automatic Data recording
    logEntries2: |-      
      During sailing, LOGBOOK creates a **log entry** **every full** or **half hour** (depending on the selected setting). LOGBOOK detects whether the ship is **moving or not** and **automatically creates an entry** both when departing and when stopping. 
    
    entry_30min_title: |-
      Automatic Data recording
    entry_30min: |-      
      During sailing, LOGBOOK creates a **log entry** **every full** or **half hour** (depending on the selected setting).

    manualEntry_title: |-
      Manual Log Entries
    manualEntry: |-      
      Regardless of the conditions selected in the **NMEA settings** dialog on the **Conditions** card for automatic log entries, a log entry can be triggered **manually** at any time with the **+ Log button** to the **left of the log row**. A click on the button creates a log entry with the **current NMEA data** and the **current ship status**. 

    manualEntry1a_title: |-
      Manual Log Entries
    manualEntry1a: |-      
      For manual log entries with commonly used options, click the **arrow button** to open the menu.

    manualEntry1b_title: |-
      Manual Log Entries
    manualEntry1b: |-      
      The menu shows various options for manual log entries. **Manual log entries** are useful, for example, when **sails** are set or taken down or when an **observation** is to be recorded.

    sail_entry1_title: |-
      Manual Log Entries
    sail_entry1: |-      
      Select the **Mainsail** button to document setting the mainsail.

    sail_entry2_title: |-
      Manual Log Entries – Sails
    sail_entry2: |-      
      After selecting **Mainsail**, the **Save sails** button appears. If needed you can select a foresail too. Click it to save the sails changes and create the log entry.

    sail_entry3_title: |-
      Manual Log Entries
    sail_entry3: |-      
      **Mainsail** and **Genoa** had been set.  

      The **comment text** in the header of the log entry is generated automatically based on the selected sails.

    selectSail2_title: |-
      Manual Log Entries – Sails
    selectSail2: |-      
      Click **Save sails** to confirm the sail selection and create the log entry.

    selectSail3_title: |-
      Manual Log Entries – Sails
    selectSail3: |-      
      The sail changes are being saved...

    selectSail4_title: |-
      Manual Log Entries
    selectSail4: |-      
      The sail change has been recorded. The log entry shows which sails were set.

    logEntries3_title: |-
      Automatic Data recording
    logEntries3: |-      
      If the NMEA data contains **engine data**, an entry is also created each time an **engine is turned on or off**. 

    logEntries4_title: |-
      Additions to Log Entries
    logEntries4: |-      
      To supplement a log entry, click on the entry's row. The possible information is divided across **3 cards**. Fields with automatically captured data cannot be edited. Editable fields are easily recognized by the highlighting of the field area. Automatic log entries can be supplemented for **a maximum of 60 minutes** as long as there is no new automatic log entry.
 
    logEntries5_title: |-
      Additions to Log Entries
    logEntries5: |-      
      In addition to the data automatically captured via NMEA, each log entry can also contain some information that is not included in the NMEA, for example, changes to the **sails**, and a **comment** as well as **weather observations**. All this you be must enter manually. 

    logEntries6_title: |-
      Additions to Log Entries
    logEntries6: |-      
      On the **Environment** card you can document observations of **weather**, **sea state** and **currents**, **air temperature** and more. 
 
    logEntries7_title: |-
      Automatic Log Entries
    logEntries7: |-      
      If activated in the **NMEA settings** dialog on the **Conditions** card, LOGBOOK will automatically create log entries for course changes. 
 
    manualEntry3_title: |-
      Manual Log Entry for Engine Status
    manualEntry3: |-      
      If the NMEA does not contain **engine data**, a **manual log entry** should be created when an **engine is turned on or off**. Use the **engine buttons** in the menu of the **Log button** to change the status of the engine and create a **manual log entry**. The icon on the button shows the **current engine status** and is green when the engine is one.       

    manualEntry4_title: |-
      Manual Log Entry for Engine Status
    manualEntry4: |-      
      In the manual log entry, the **RPM** can then also be entered for each engine. 

    logEntries8_title: |-
      Automatic Log Entries
    logEntries8: |-      
      Enjoy sailing while LOGBOOK **automatically** creates **log entries** for you. Whenever needed or desired, you can add **additional information** or make **manual log entries**.

    endDay_title: |-
      End of Day
    endDay: |-      
      Whenever the ship has not been moving for a certain period of time, a **stop button** appears in the log row.            
  
    endDay2_title: |-
      End of Day
    endDay2: |-      
      The **stop button** opens a dialog in which you can record the stop as an intermediate stop or end sailing and thus the day. 
        
    endDay3_title: |-
      End of Day
    endDay3: |-            
      To end the day, select the **End of day** option. Then enter the name of your **current location**. Choose whether you are **anchoring**, in a **harbor**, etc.  
      
    endDay4_title: |-
      End of Day
    endDay4: |-      
      After closing the dialog, the symbol for the **selected ship status** is displayed in the log entry's row. LOGBOOK changes the **ship status** of the log entry that was automatically **created when stopping**. This gives you the opportunity to record details for this entry later.          
  
    endDay5_title: |-
      End of Day
    endDay5: |-      
      With the selection of **End of day**, the LOGBOOK day is ended. LOGBOOK **deactivates the automatic log entries** and you can now, if desired, turn off your NMEA network and close the LOGBOOK app. Then **turning off data recording** is documented in a **log entry** (without NMEA data).           

    endDay6_title: |-
      End of Day
    endDay6: |-      
      If **data recording is turned off**, NMEA menu button gets an orange border. The Automatic logging menu option is disabled. 

    endDay7_title: |-
      End of Day
    endDay7: |-      
      If you keep the **NMEA connection alive** and you later or on next day start sailing again, LOGBOOK will **recognize** and document the **departure** again and automatically create log entries as before. With connection alive the NMEA button shows a green border but the Automatic logging menu item is disabled.   
      
    nextDay_title: |-
      The Next Day
    nextDay: |-      
      The new day is already prepared. On the **Day card**, everything is filled in as it was on the previous day, and the **Start location** is the destination of the previous day. If necessary, you can still make changes to the crew, etc.   
  
    nextDay2_title: |-
      The Next Day
    nextDay2: |-      
      On next day all is ready for starting. Whether you had closed and reopened the LOGBOOK app or it was open the whole time, if the **NMEA data source** is **turned on**, you can immediately continue with the **log entries** for the new day.   

    nextDay3_title: |-
      The Next Day
    nextDay3: |-      
      LOGBOOK **automatically reconnects** to the **NMEA** after closing the app and/or turning off the NMEA data source and regularly searches for NMEA data. As soon as the connection can be re-established, the **NMEA button shows green** again. 
      
      If the button's marker remains orange, you must activate the **NMEA connection toggle switch** from the **NMEA menu** again.   

    nextDay4_title: |-
      The Next Day
    nextDay4: |-      
      And as soon as the **ship starts moving** again, **automatic log entries** are started, and at the end of the sailing, the necessary steps to end the day and automatic log entries can be performed again in the **stop dialog**.   

    final2_title: |-
      Learn more about LOGBOOK
    final2: |-
      Check out our other screencasts and learn more about the use of **LOGBOOK** in daily practice. (More screencasts coming soon.) 

ui:
  previous: Previous
  next: Next
  start_image: /demo/Title-Concept.svg
  start_solo_image: /demo/Start.svg
  quickstartChapter: |-
    QuickStart - Preparations
  quickstartChapter2: |-
    QuickStart - First Passsage